import { useLayoutEffect, useRef } from 'react';

/**
 * Places a stable host node into the DOM at this component's mount point.
 * Uses `appendChild` in a `useLayoutEffect` so the host node is moved
 * (not cloned) when `TeleportTarget` remounts in a different position,
 * preserving the React subtree portaled into the host.
 *
 * `useTeleport().Target` uses this component internally. `TeleportTarget`
 * exists as the direct component form for call sites that manage their own
 * host node (e.g. `PageLayout`, which holds hosts in context).
 *
 * @internal
 */
export function TeleportTarget({ host }: { host: HTMLDivElement }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || host.parentNode === container) {
      return;
    }
    if (
      process.env.NODE_ENV !== 'production' &&
      host.parentNode !== null &&
      container.ownerDocument.contains(host.parentNode)
    ) {
      // Moving a host out of a parent that is still mounted in the document
      // silently steals the portaled subtree from whoever rendered the other
      // Target. Almost always a bug: two TeleportTargets rendering for the
      // same host at the same time.
      //
      // Legitimate single-Target moves (e.g. a layout switching between
      // split/drawer branches) don't hit this path because React unmounts the
      // old Target's wrapper before the new Target's effect runs, leaving the
      // host's previous parent detached from the document.
      // eslint-disable-next-line no-console
      console.error(
        '[TeleportTarget] Host node already has a live parent elsewhere; ' +
          'moving it will detach the subtree from its previous mount point. ' +
          'This usually indicates two TeleportTargets rendering for the same host.',
      );
    }
    container.appendChild(host);
  });

  return <div ref={containerRef} style={{ display: 'contents' }} />;
}
